import { formatOdds, type OddsFormat } from '@geni/contracts/odds-format';
import type { Teaser, TeaserSport } from './teaser.js';
import type { WidgetViewModel } from './view-model.js';

/**
 * The screen, as a tree of plain objects.
 *
 * Two reasons this exists rather than the element writing DOM directly.
 *
 * **It is testable without a browser.** The repository runs vitest in a node
 * environment and has no DOM implementation installed. Rendering to data means
 * every question worth asking — is the RG notice shown, is the input closed in
 * a prohibited jurisdiction, is the age filled in — is answered by an assertion
 * on a value rather than by installing jsdom to inspect an attribute.
 *
 * **There is no way to inject markup.** A spec carries `text`, and the binder
 * assigns it with `textContent`. There is no `html` field, no
 * `dangerouslySetInnerHTML`, nothing that takes a string and parses it. The
 * assistant's reply comes from a language model and is screened but never
 * trusted, and "render it as text" stops being a rule someone has to remember
 * when the type has nowhere to put anything else.
 *
 * The presentation layer added in chunk 13 changed the shape of this tree and
 * not that property: the icons below are `<path>` elements with a `d`
 * attribute, which is geometry, and every string a bettor reads still arrives
 * through `text`.
 */

/**
 * Lives here rather than in `element.ts`, which is where it would read more
 * naturally, because `element.ts` already imports `ElementSpec` from this file
 * and the pair made a cycle — `pnpm boundaries` fails on one, and rightly: a
 * cycle at the base of the widget makes the layering it advertises unprovable.
 * It is part of the spec's vocabulary anyway: it is what `ns` may be set to.
 */
export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

export interface ElementSpec {
  tag: string;
  /** Text content. Assigned with textContent, never parsed as markup. */
  text?: string;
  className?: string;
  /** Plain attributes: aria-*, lang, disabled, type. Never event handlers. */
  attrs?: Record<string, string>;
  /**
   * A namespace, for SVG. Inherited by children.
   *
   * The only reason this exists is that icons drawn with `createElement` are
   * unknown HTML elements that render as nothing. It carries no new power: an
   * `<svg>` built here still has no field that could hold markup.
   */
  ns?: string;
  children?: ElementSpec[];
}

const el = (
  tag: string,
  props: Omit<ElementSpec, 'tag'> = {},
): ElementSpec => ({ tag, ...props });

/**
 * An icon, as geometry.
 *
 * Decorative by construction — `aria-hidden` on every one of them, because each
 * sits next to a label that already says the same thing in the bettor's
 * language, and an icon that announces itself makes a screen reader repeat it.
 */
const icon = (paths: readonly Record<string, string>[]): ElementSpec =>
  el('svg', {
    ns: SVG_NAMESPACE,
    attrs: { viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' },
    children: paths.map((attrs) => el('path', { ns: SVG_NAMESPACE, attrs })),
  });

/**
 * The Geni mark, traced from the artwork rather than redrawn.
 *
 * `geni-logo.png` at the repository root is the drawing; `tools-trace-logo.py`
 * turns its pixels into these outlines and printed them straight into this
 * file. Nothing below was placed by eye, which matters because three
 * hand-fitted attempts each lost something — the fork at the foot of the I, or
 * the terminal that turns back inside the bowl and is the only reason the
 * ellipse reads as a G. Re-run the script rather than nudging a number here;
 * a coordinate edited by hand is the first step back to a redrawing.
 *
 * Filled outlines with `evenodd`, because `icon()` paints every path with
 * `fill: currentColor` and a stroked path would come out invisible. The
 * counters inside the bowl are holes in the same path, which is what `evenodd`
 * is for.
 *
 * The chalk speckle is smoothed away by the script — traced raw it is several
 * hundred single-pixel steps, jagged on screen and heavy in the bundle. Every
 * point still sits within a pixel of where the chalk put it.
 */
const GENI_ICON = [
  // The symbol: the forked I, and the G whose bowl it stands in.
  {
    'fill-rule': 'evenodd',
    d:
      'M12.76 2.10Q12.76 2.10 12.84 2.40Q12.91 2.69 12.97 3.57Q13.04 4.44 12.99 5.63Q12.95 6.83 12.95 8.65Q12.95 10.47 13.00 10.96Q13.05 11.45 13.15 11.49Q13.26 11.54 14.20 11.54Q15.15 11.54 16.24 11.64Q17.33 11.74 17.99 11.84Q18.66 11.93 18.87 11.98Q19.08 12.04 19.35 12.17Q19.61 12.30 19.53 12.32Q19.44 12.33 18.84 12.20Q18.24 12.07 17.33 12.00Q16.42 11.93 16.21 11.88Q16.01 11.82 14.71 11.80Q13.41 11.79 13.26 11.85Q13.11 11.90 13.12 13.48Q13.13 15.06 13.15 15.14Q13.16 15.22 13.22 15.27Q13.27 15.31 13.69 15.29Q14.11 15.27 15.19 15.10Q16.28 14.93 16.79 14.80Q17.30 14.68 17.69 14.53Q18.08 14.37 18.16 14.26Q18.23 14.14 18.16 14.06Q18.08 13.97 17.51 13.98Q16.93 13.99 16.16 13.92Q15.38 13.85 15.83 13.79Q16.29 13.72 17.36 13.67Q18.43 13.62 18.90 13.68Q19.37 13.74 19.45 13.82Q19.53 13.90 19.49 13.97Q19.46 14.05 19.22 14.23Q18.98 14.42 18.39 14.63Q17.80 14.84 16.66 15.09Q15.52 15.34 14.45 15.45Q13.38 15.56 13.27 15.61Q13.17 15.67 13.13 15.84Q13.08 16.01 13.04 16.76Q13.00 17.51 12.84 18.89Q12.68 20.27 12.64 21.72Q12.61 23.17 12.54 23.54Q12.47 23.92 12.44 23.84Q12.41 23.76 12.45 23.05Q12.49 22.35 12.52 19.70Q12.55 17.05 12.55 16.56Q12.54 16.07 12.50 15.88Q12.47 15.68 12.22 15.66Q11.98 15.63 11.84 15.74Q11.69 15.85 11.65 16.10Q11.61 16.35 11.59 17.13Q11.58 17.90 11.54 16.91Q11.50 15.92 11.45 15.83Q11.40 15.73 11.09 15.69Q10.78 15.65 10.35 15.68Q9.92 15.70 9.29 15.67Q8.67 15.64 7.73 15.54Q6.80 15.43 6.61 15.38Q6.42 15.32 6.12 15.32Q5.82 15.32 5.50 15.19Q5.18 15.05 5.09 14.93Q5.00 14.81 4.75 14.67Q4.51 14.54 4.46 14.43Q4.41 14.33 4.51 14.24Q4.61 14.15 4.77 13.81Q4.93 13.47 5.23 13.23Q5.53 13.00 5.73 12.96Q5.94 12.92 6.84 12.56Q7.74 12.20 8.54 12.01Q9.35 11.81 10.37 11.71Q11.39 11.61 11.49 11.56Q11.59 11.51 12.12 6.92Q12.65 2.33 12.70 2.22Q12.75 2.10 12.76 2.10ZM11.11 11.96Q11.13 11.96 11.29 11.96Q11.45 11.97 11.49 12.08Q11.54 12.19 11.50 13.56Q11.47 14.94 11.45 15.10Q11.43 15.25 11.36 15.31Q11.29 15.36 9.99 15.31Q8.70 15.27 8.18 15.18Q7.66 15.09 7.35 15.01Q7.04 14.94 6.79 14.81Q6.54 14.68 6.38 14.64Q6.22 14.60 6.03 14.49Q5.84 14.38 5.79 14.28Q5.74 14.17 5.76 14.09Q5.78 14.01 5.90 13.94Q6.01 13.87 6.07 13.70Q6.13 13.53 6.46 13.37Q6.79 13.20 6.96 13.07Q7.13 12.94 7.80 12.70Q8.48 12.45 9.79 12.20Q11.10 11.96 11.11 11.96ZM12.22 11.87Q12.23 11.86 12.36 11.88Q12.48 11.89 12.51 12.01Q12.55 12.12 12.53 13.47Q12.51 14.82 12.49 15.04Q12.47 15.25 12.40 15.31Q12.33 15.36 12.08 15.37Q11.84 15.38 11.78 15.26Q11.73 15.15 11.74 14.47Q11.76 13.78 11.84 12.95Q11.92 12.13 11.96 12.05Q12.00 11.96 12.11 11.92Q12.21 11.87 12.22 11.87ZM12.23 10.18Q12.24 10.17 12.32 10.17Q12.40 10.16 12.45 10.24Q12.50 10.32 12.48 10.89Q12.46 11.45 12.33 11.48Q12.19 11.50 12.12 11.44Q12.04 11.37 12.13 10.78Q12.22 10.19 12.23 10.18Z',
  },
  // The point above it.
  {
    'fill-rule': 'evenodd',
    d:
      'M12.73 0.02Q12.75 0.02 12.90 0.06Q13.05 0.09 13.12 0.19Q13.20 0.29 13.17 0.46Q13.15 0.64 13.00 0.73Q12.84 0.83 12.75 0.82Q12.66 0.80 12.57 0.73Q12.48 0.65 12.41 0.39Q12.35 0.12 12.53 0.07Q12.72 0.02 12.73 0.02Z',
  },
] as const;

const CLOSE_ICON = [{ d: 'M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5' }] as const;

const SEND_ICON = [
  {
    d:
      'M3.2 20.3 20.7 12.8c.8-.3.8-1.4 0-1.7L3.2 3.6c-.7-.3-1.4.3-1.2 1.1L3.9 11 13 12l-9.1 1-1.9 6.2c-.2.8.5 1.4 1.2 1.1Z',
  },
] as const;

/**
 * An amount of money, in the bettor's own format and the operator's currency.
 *
 * The single money formatter in the widget, on purpose. Two of them is how a
 * balance and a stake end up written differently on the same card — and the
 * server has its own copy of this for the assistant's sentences, which is why
 * both are `Intl.NumberFormat` with the same options rather than hand-built
 * strings.
 */
export function formatMoney(amount: number, currency: string, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency }).format(amount);
  } catch {
    // An unusual tag or an unknown currency code must not take the panel down.
    // Same reasoning as formatPrice below.
    return `${amount.toFixed(2)} ${currency}`.trim();
  }
}

/**
 * A price as the page writes prices, in the bettor's own number format.
 *
 * Decimal is two decimals exactly as this has always printed; the other formats
 * are the ones a sportsbook's odds menu offers. The price itself is decimal
 * everywhere else in the product — this is only how it is written down.
 */
export function formatPrice(price: number, lang: string, odds: OddsFormat = 'decimal'): string {
  return formatOdds(price, odds, lang);
}

/**
 * The layers that make up the glass: aura and tint behind, grain and shimmer
 * above.
 *
 * `geni-glass-layer` carries the tint and the `backdrop-filter`, and it is a
 * child rather than the panel itself for a reason that is a rendering fact
 * rather than a preference: an element with both `border-radius` and
 * `backdrop-filter` paints its filtered backdrop into the square border box, so
 * a hard-edged corner shows just outside each rounded one. The panel's
 * `overflow: hidden` cannot clip its own backdrop, but it does clip a child's.
 * Written out in `styles.ts` beside the rule.
 */
function materialLayers(): ElementSpec[] {
  return [
    el('div', { className: 'geni-aura', attrs: { 'aria-hidden': 'true' } }),
    el('div', { className: 'geni-glass-layer', attrs: { 'aria-hidden': 'true' } }),
    el('div', { className: 'geni-grain', attrs: { 'aria-hidden': 'true' } }),
    el('div', { className: 'geni-shimmer', attrs: { 'aria-hidden': 'true' } }),
  ];
}

/**
 * The mini-stage on the collapsed pill.
 *
 * Three elements, and no more: a sport glyph that is always there, a dot that
 * travels when an action plays, and a ring that answers when it arrives. The
 * court, the goal-mouth and the stick figure are gone — at 48x34 a figure with
 * limbs was noise pretending to be detail, and a drawn pitch competed with the
 * pill's own material instead of sitting on it.
 *
 * The glyph never leaves. That is the difference between a control and an
 * animation: for most of the cycle this is a resting icon, which is what it
 * should look like when nobody is watching it move.
 *
 * Still geometry, on the same terms as everything else `ElementSpec` can carry
 * — circles and paths, no field that takes a string somebody parses.
 */
function renderTeaserStage(teaser: Teaser): ElementSpec {
  const shape = (tag: string, attrs: Record<string, string>, className?: string): ElementSpec =>
    el(tag, { ns: SVG_NAMESPACE, attrs, ...(className === undefined ? {} : { className }) });

  /**
   * One glyph per sport, monoline, drawn on a 14-unit circle at (12, 17).
   *
   * Deliberately the ball rather than the venue: a ball is recognisable at a
   * glance at this size and a stadium is not, and it is the object the action
   * below actually concerns.
   */
  const glyphs: Record<TeaserSport, ElementSpec[]> = {
    // Football: the panel seams, reduced to the three that read as a football.
    SOCCER: [
      shape('circle', { cx: '12', cy: '17', r: '6.4' }, 'geni-stage-glyph'),
      shape(
        'path',
        { d: 'M12 12.4l3.4 2.5-1.3 4h-4.2l-1.3-4zM12 10.6v1.8M6.4 16l2.2.9M17.6 16l-2.2.9M9 22.5l.9-2.6M15 22.5l-.9-2.6', fill: 'none' },
        'geni-stage-glyph-detail',
      ),
    ],
    /**
     * American football: the only shape here that is not a circle, which is
     * what carries the recognition — four of the others differ by their
     * markings alone, and a fifth circle would be read as whichever of them the
     * eye reached for first.
     *
     * Drawn three times before this one, and the failures are worth keeping
     * because they were all the same failure. A circle with laces on it was the
     * first: the problem restated, a ball inside a ball. A horizontal pointed
     * oval was the second, and at launcher size it read unmistakably as an
     * *eye* — the tips are the lids and a lace ladder in the middle is the
     * pupil. Adding the two end bands was the third, and it made a better eye.
     *
     * Tilting it is what fixes it, and nothing else did. These glyphs are drawn
     * in outline rather than filled — see `.geni-stage-glyph` — so the
     * silhouette is doing all the work, and a horizontal almond in outline is
     * an eye whatever is put inside it. On the diagonal that reading is simply
     * unavailable: no eye sits at thirty-five degrees. It is also how a thrown
     * ball actually looks, which is what the stage beside it is about.
     *
     * Two quadratics between the tips, laces perpendicular to the long axis.
     */
    AMERICAN_FOOTBALL: [
      shape(
        'path',
        { d: 'M6.8 20.7Q15.9 22.6 17.2 13.3 8.1 11.4 6.8 20.7z' },
        'geni-stage-glyph',
      ),
      shape(
        'path',
        {
          d: 'M10 18.4L14 15.6M10.1 17L11.3 18.8M11.4 16.1L12.6 17.9M12.7 15.2L13.9 17',
          fill: 'none',
        },
        'geni-stage-glyph-detail',
      ),
    ],
    // Basketball: the two seams that make a sphere read as a basketball.
    BASKETBALL: [
      shape('circle', { cx: '12', cy: '17', r: '6.4' }, 'geni-stage-glyph'),
      shape(
        'path',
        { d: 'M5.6 17h12.8M12 10.6v12.8M7.5 12.5a9 9 0 0 0 0 9M16.5 12.5a9 9 0 0 1 0 9', fill: 'none' },
        'geni-stage-glyph-detail',
      ),
    ],
    // Tennis: the single curved seam, which is the whole of a tennis ball.
    TENNIS: [
      shape('circle', { cx: '12', cy: '17', r: '6.4' }, 'geni-stage-glyph'),
      shape(
        'path',
        { d: 'M7.6 12.5a7 7 0 0 1 0 9M16.4 12.5a7 7 0 0 0 0 9', fill: 'none' },
        'geni-stage-glyph-detail',
      ),
    ],
    // Golf: the flag, because a golf ball and a cue ball are the same circle.
    GOLF: [
      shape(
        'path',
        { d: 'M10 23.5V10.5l6.5 2.6-6.5 2.6', fill: 'none' },
        'geni-stage-glyph-detail',
      ),
      shape('circle', { cx: '14.4', cy: '22.4', r: '1.9' }, 'geni-stage-glyph'),
    ],
    // CS:GO: a crosshair. The one sport here with no ball, and the reticle is
    // what a player actually looks at.
    ESPORTS: [
      shape('circle', { cx: '12', cy: '17', r: '5.6' }, 'geni-stage-glyph'),
      shape(
        'path',
        { d: 'M12 8.6v3.2M12 22.2v3.2M3.6 17h3.2M17.2 17h3.2', fill: 'none' },
        'geni-stage-glyph-detail',
      ),
    ],
  };

  return el('span', {
    className: `geni-stage geni-stage--${teaser.action}`,
    attrs: { 'aria-hidden': 'true' },
    children: [
      el('svg', {
        ns: SVG_NAMESPACE,
        attrs: { viewBox: '0 0 48 34', 'aria-hidden': 'true', focusable: 'false' },
        children: [
          ...glyphs[teaser.sport],
          // Where the action lands. A ring rather than a goal or a hoop: one
          // shape that means "here" in every sport, which is what lets five
          // stages share one set of keyframes.
          shape('circle', { cx: '38', cy: '17', r: '4.2' }, 'geni-stage-target'),
          shape('circle', { cx: '0', cy: '0', r: '1.9' }, 'geni-stage-ball'),
        ],
      }),
    ],
  });
}

/**
 * The collapsed launcher.
 *
 * Exported because it is the one thing that has to be drawable *before* there
 * are any strings. The rule that nothing renders until the bundle arrives is
 * about not flashing English at a Turkish bettor, and it is right — but the
 * launcher is what opens the session that fetches the bundle, so applying it
 * there is a deadlock: nothing to click, so nothing ever loads.
 *
 * The way out is that this state needs no translated string. At rest it is the
 * mark in a circle, exactly as the mockup has it; the only text is its
 * accessible name, and the default for that is the product's name, which is a
 * proper noun and the same in every language. An operator who wants a word
 * instead sets `launcher-label`. Once the bundle lands, `renderWidget` draws
 * this again with the real string.
 *
 * The alternative — opening a session on page load so the strings are there
 * early — was rejected: session minting is rate limited per origin and shared
 * across everyone on it, so a busy casino page would exhaust its own budget on
 * visitors who never opened the assistant.
 */
export function renderLauncher(
  label: string,
  teaser: Teaser | null = null,
  /**
   * A translated description of what pressing it does.
   *
   * Absent before the string tables arrive, which is the one moment there is
   * nothing honest to say in the bettor's language. It is a `title` rather than
   * the accessible name because the name has to contain the visible text —
   * "click Geni AI" must work for a voice-control user.
   */
  hint?: string,
): ElementSpec {
  return el('button', {
    className:
      teaser === null
        ? 'geni-launcher'
        // The sport sits HERE rather than on the stage, and it has to: it tints
        // the pill's ambient glow as well as the glyph, and a custom property
        // set on a child cannot reach its parent.
        : `geni-launcher geni-launcher--staged geni-sport--${teaser.sport}`,
    attrs: {
      type: 'button',
      'aria-expanded': 'false',
      // The label is beside the mark rather than revealed on hover now, so the
      // accessible name and the visible one finally say the same thing — but it
      // stays here because a pointer user hovering and a screen-reader user
      // listening should still be told the same thing, and because the pill
      // renders before any strings exist.
      'aria-label': label,
      ...(hint === undefined || hint === '' ? {} : { title: hint }),
    },
    children: [
      /**
       * The material, in three layers under the content.
       *
       * The blur is on a CHILD and not on the button, which is a rendering fact
       * rather than a preference: an element carrying both a border-radius and
       * a backdrop-filter paints its filtered backdrop into the square border
       * box, so a hard corner shows just outside each rounded one. The button's
       * own `overflow: hidden` cannot clip its own backdrop — but it does clip
       * a child's. The panel learned this in chunk 13; the launcher is the most
       * rounded thing in the widget, so it would have shown it worst.
       */
      ...(teaser === null
        ? []
        : [
            el('span', { className: 'geni-launcher-glass', attrs: { 'aria-hidden': 'true' } }),
            el('span', { className: 'geni-launcher-glow', attrs: { 'aria-hidden': 'true' } }),
            el('span', { className: 'geni-launcher-sheen', attrs: { 'aria-hidden': 'true' } }),
          ]),
      // The stage replaces the mark when there is a sport to draw. Both, side
      // by side, would be two logos on a 180px pill.
      teaser === null
        ? el('span', {
            className: 'geni-launcher-mark',
            attrs: { 'aria-hidden': 'true' },
            children: [icon(GENI_ICON)],
          })
        : renderTeaserStage(teaser),
      el('span', { className: 'geni-launcher-label', text: label }),
      el('span', { className: 'geni-launcher-status', attrs: { 'aria-hidden': 'true' } }),
    ],
  });
}

function renderHeader(vm: WidgetViewModel): ElementSpec {
  return el('header', {
    className: 'geni-header',
    children: [
      el('div', {
        className: 'geni-brand',
        children: [
          el('span', {
            className: 'geni-mark',
            attrs: { 'aria-hidden': 'true' },
            children: [icon(GENI_ICON)],
          }),
          el('div', {
            className: 'geni-brand-text',
            children: [
              el('h2', { className: 'geni-title', text: vm.title }),
              el('p', { className: 'geni-subtitle', text: vm.subtitle }),
            ],
          }),
        ],
      }),
      el('div', {
        className: 'geni-header-actions',
        children: [
          el('button', {
            className: 'geni-icon-button geni-close',
            attrs: { type: 'button', 'aria-label': vm.closeLabel },
            children: [icon(CLOSE_ICON)],
          }),
        ],
      }),
    ],
  });
}

function renderMessages(vm: WidgetViewModel): ElementSpec {
  return el('ol', {
    className: 'geni-messages',
    children: vm.messages.map((message) =>
      el('li', {
        className: `geni-row geni-row--${message.from}`,
        children: [
          // The class on this element is what carries the bubble, and the reply
          // is its textContent. Nothing wraps it that could reinterpret it.
          el('div', {
            className: `geni-message geni-message--${message.from}`,
            text: message.text,
          }),
          ...(message.at === undefined
            ? []
            : [el('span', { className: 'geni-message-time', text: message.at })]),
        ],
      }),
    ),
  });
}

function renderThinking(label: string): ElementSpec {
  return el('p', {
    className: 'geni-thinking',
    attrs: { role: 'status' },
    children: [
      el('span', {
        className: 'geni-thinking-dots',
        attrs: { 'aria-hidden': 'true' },
        children: [el('span'), el('span'), el('span')],
      }),
      el('span', { text: label }),
    ],
  });
}

/**
 * Openers, for a bettor who has not typed anything yet.
 *
 * Buttons rather than decoration: each one is sent verbatim as the bettor's own
 * message, which is why the strings behind them are market-shaped ("Map 1
 * winner") rather than chatty. A chip that read "Ask me anything!" would put
 * that sentence in the conversation as though a person had typed it.
 *
 * Deliberately its own class rather than reusing `.geni-chip`: an amount and
 * a question are different kinds of thing, and a bettor who has learnt that the
 * accented pills are money should not find one that is a sentence.
 */
/**
 * Two taps, and the asymmetry between them is the design.
 *
 * Approval is one press and collapses to a thank-you. Disapproval opens three
 * reasons, because that is where detail is worth asking for: nobody
 * introspects on success, and a follow-up after a good answer would cost most
 * of the taps to collect sentences saying "it was fine".
 *
 * Rendered small and unaccented on purpose. This is a comment on the answer,
 * not an action in the bet — it must not compete with a confirm button on a
 * screen where pressing something moves money.
 */
function renderFeedback(feedback: NonNullable<WidgetViewModel['feedback']>): ElementSpec {
  if (feedback.thanks !== null) {
    return el('p', {
      className: 'geni-feedback geni-feedback--done',
      text: feedback.thanks,
      attrs: { role: 'status' },
    });
  }

  if (feedback.reasons.length > 0) {
    return el('div', {
      className: 'geni-feedback geni-feedback--why',
      children: feedback.reasons.map((reason) =>
        el('button', {
          className: 'geni-feedback-reason',
          text: reason.label,
          attrs: { type: 'button', 'data-feedback-reason': reason.id },
        }),
      ),
    });
  }

  return el('div', {
    className: 'geni-feedback',
    children: [
      el('button', {
        className: 'geni-feedback-vote',
        text: feedback.helpful,
        attrs: { type: 'button', 'data-verdict': 'HELPFUL' },
      }),
      el('button', {
        className: 'geni-feedback-vote',
        text: feedback.notHelpful,
        attrs: { type: 'button', 'data-verdict': 'NOT_HELPFUL' },
      }),
    ],
  });
}

/** The free-text form, over the conversation rather than inside it. */
function renderFeedbackForm(form: NonNullable<WidgetViewModel['feedbackForm']>): ElementSpec {
  return el('div', {
    className: 'geni-feedback-form',
    attrs: { role: 'group', 'aria-label': form.title },
    children: [
      el('p', { className: 'geni-feedback-form-title', text: form.title }),
      el('textarea', {
        className: 'geni-feedback-text',
        attrs: { rows: '3', placeholder: form.placeholder, maxlength: '1000' },
      }),
      el('div', {
        className: 'geni-feedback-form-actions',
        children: [
          el('button', {
            className: 'geni-feedback-cancel',
            text: form.cancel,
            attrs: { type: 'button' },
          }),
          el('button', {
            className: 'geni-feedback-send',
            text: form.send,
            attrs: { type: 'button' },
          }),
        ],
      }),
    ],
  });
}

function renderStarters(starters: NonNullable<WidgetViewModel['starters']>): ElementSpec {
  return el('section', {
    className: 'geni-starters',
    children: [
      el('p', { className: 'geni-starters-title', text: starters.title }),
      el('div', {
        className: 'geni-starter-list',
        children: starters.prompts.map((prompt) =>
          el('button', {
            className: 'geni-starter',
            text: prompt,
            // The text is the payload. Read back off the button when it is
            // clicked, so nothing has to keep an index in step with the tree.
            attrs: { type: 'button', 'data-prompt': prompt },
          }),
        ),
      }),
    ],
  });
}

/**
 * The stake step: what is available, a few one-tap amounts, and a field.
 *
 * The field is deliberately last and the suggestions first. Typing an amount is
 * the fiddliest thing anybody does on a phone, and the suggestions are already
 * bounded by the balance — so the easy paths are the ones that cannot overdraw
 * somebody, and the one that can is the one they have to type out.
 *
 * `value` is set as an attribute rather than kept in the DOM, because the tree
 * is rebuilt on every state change: without it, tapping a suggestion would
 * update the card and silently blank the box next to it.
 */
function renderStake(stake: NonNullable<WidgetViewModel['stake']>, lang: string): ElementSpec {
  const currency = stake.currency ?? '';
  const money = (amount: number): string => formatMoney(amount, currency, lang);

  const children: ElementSpec[] = [];

  // The balance, stated once and plainly. Not styled as a warning even when
  // the amount is over it — the number itself is neutral, and the sentence
  // below is where the problem is named.
  if (stake.balance !== null) {
    children.push(
      el('p', {
        className: 'geni-stake-balance',
        children: [
          el('span', { className: 'geni-stake-balance-label', text: stake.balanceLabel }),
          el('span', {
            className: 'geni-stake-balance-value',
            text: money(stake.balance),
          }),
        ],
      }),
    );
  }

  if (stake.problem !== null) {
    children.push(
      el('p', {
        className: 'geni-stake-problem',
        // A status rather than an alert: it is information about what will
        // work, not an error the bettor caused.
        attrs: { role: 'status' },
        text: stake.problem,
      }),
    );
  }

  if (stake.actions.length > 0) {
    children.push(
      el('div', {
        className: 'geni-chips',
        children: stake.actions.map((action) =>
          action.kind === 'TOP_UP'
            ? el('button', {
                className: 'geni-chip geni-chip--topup',
                text: action.label,
                attrs: {
                  type: 'button',
                  'data-stake-action': 'top-up',
                  ...(stake.inputEnabled ? {} : { disabled: 'disabled' }),
                },
              })
            : el('button', {
                className: 'geni-chip',
                // The label template carries {amount}; the money formatter is
                // here, so the substitution is here too.
                text: (action.label ?? '').replace(
                  '{amount}',
                  money(action.amount ?? 0),
                ),
                attrs: {
                  type: 'button',
                  'data-stake-amount': String(action.amount ?? 0),
                  ...(stake.inputEnabled ? {} : { disabled: 'disabled' }),
                },
              }),
        ),
      }),
    );
  }

  children.push(
    el('div', {
      className: 'geni-stake-field',
      children: [
        el('label', { className: 'geni-stake-label', text: stake.label }),
        el('input', {
          className: 'geni-stake-input',
          attrs: {
            type: 'text',
            inputmode: 'decimal',
            autocomplete: 'off',
            'aria-label': stake.label,
            placeholder: stake.placeholder,
            ...(stake.amount === null ? {} : { value: String(stake.amount) }),
            ...(stake.inputEnabled ? {} : { disabled: 'disabled' }),
          },
        }),
      ],
    }),
  );

  return el('section', { className: 'geni-stake', children });
}

/**
 * The one-click confirmation card.
 *
 * Everything the bettor is agreeing to, on one surface, above the button that
 * agrees to it: the sport, the match, what was picked, the price, the amount
 * and what it returns. Nothing here is fetched at press time — every figure is
 * already on screen, which is what makes "one click" a shortcut rather than a
 * leap.
 */
function renderBetCard(
  card: NonNullable<WidgetViewModel['betCard']>,
  lang: string,
  odds: OddsFormat,
): ElementSpec {
  const money = (amount: number): string => formatMoney(amount, card.currency, lang);

  const row = (label: string, value: string, modifier: string): ElementSpec =>
    el('p', {
      className: `geni-card-row geni-card-row--${modifier}`,
      children: [
        el('span', { className: 'geni-card-label', text: label }),
        el('span', { className: 'geni-card-value', text: value }),
      ],
    });

  return el('section', {
    className: 'geni-card',
    children: [
      el('div', {
        className: 'geni-card-head',
        children: [
          el('span', { className: 'geni-card-title', text: card.title }),
          ...(card.sport === null
            ? []
            : [el('span', { className: 'geni-card-sport', text: card.sport })]),
        ],
      }),
      ...(card.eventLabel === ''
        ? []
        : [el('p', { className: 'geni-card-event', text: card.eventLabel })]),
      el('ul', {
        className: 'geni-card-legs',
        children: card.legs.map((leg) =>
          el('li', {
            className: 'geni-card-leg',
            children: [
              el('span', {
                className: 'geni-card-selection',
                // The same rule as the slip cards: both labels, but only once
                // when a player market says the same thing twice.
                text:
                  leg.marketLabel === leg.selectionLabel || leg.marketLabel === ''
                    ? leg.selectionLabel
                    : `${leg.marketLabel}: ${leg.selectionLabel}`,
              }),
              el('span', {
                className: 'geni-card-leg-price',
                text: formatPrice(leg.price, lang, odds),
              }),
              /**
               * A way out of one leg without abandoning the bet.
               *
               * The ticket is the last screen before money moves, and it is
               * exactly where somebody thinks "actually, not the Messi one".
               * Without this they must throw the whole slip away and describe
               * it again, which is a great deal of work to undo one tap — most
               * people would simply not bother, and would either place a bet
               * they had doubts about or place none.
               *
               * Absent rather than disabled on the last leg, because a slip
               * reduced to nothing is not a smaller bet. The route refuses it
               * too; this is the half that keeps it from being offered.
               */
              ...(card.removable
                ? [
                    el('button', {
                      className: 'geni-card-leg-remove',
                      attrs: {
                        type: 'button',
                        'data-selection-id': leg.selectionId,
                        'data-slip-id': card.slipId,
                        'aria-label': `${card.removeLabel}: ${leg.selectionLabel}`,
                        title: card.removeLabel,
                      },
                      children: [
                        // One stroke, drawn rather than typed, for the reason
                        // the plus is: a character is at the mercy of whatever
                        // font the operator's page sets.
                        el('svg', {
                          className: 'geni-card-leg-minus',
                          ns: SVG_NAMESPACE,
                          attrs: { viewBox: '0 0 12 12', width: '12', height: '12', 'aria-hidden': 'true' },
                          children: [
                            el('path', {
                              ns: SVG_NAMESPACE,
                              attrs: { d: 'M2.6 6h6.8', fill: 'none', 'stroke-width': '1.6' },
                            }),
                          ],
                        }),
                      ],
                    }),
                  ]
                : []),
            ],
          }),
        ),
      }),
      row(card.oddsLabel, formatPrice(card.totalPrice, lang, odds), 'odds'),
      row(card.stakeLabel, money(card.stake), 'stake'),
      row(card.payoutLabel, money(card.payout), 'payout'),
      el('button', {
        className: 'geni-place',
        text: card.confirmLabel,
        attrs: {
          type: 'button',
          'data-slip-id': card.slipId,
          ...(card.confirmEnabled ? {} : { disabled: 'disabled' }),
        },
      }),
    ],
  });
}

/**
 * Several matches to choose between, as chips rather than as cards.
 *
 * A full slip card is the right shape for the bet somebody has settled on: the
 * legs, the price, the button that commits it. It is the wrong shape for a
 * question. Four of them stack into a column taller than the panel, so choosing
 * between them means scrolling up and down comparing things you cannot see at
 * once — and every one of them carries a commit button, on a screen where
 * nothing has been decided yet.
 *
 * So the choice is drawn the way the stake suggestions are drawn, because it is
 * the same act: a short row of small things to press. Pressing one narrows the
 * conversation to that slip, and the card everyone expects appears then, with
 * its legs and its button, for the one bet actually under consideration.
 *
 * The price rides on the chip. It is what the bettor is choosing between when
 * the matches are otherwise alike, and leaving it for the next screen would
 * make them press each one to find out.
 */
/**
 * What to call one of several, in a line short enough to read at a glance.
 *
 * A single bet has a fixture, and the fixture is usually the answer: it is what
 * the bettor is choosing between when the assistant found their team playing
 * four times. Usually, but not always — two readings of *one* match are also a
 * choice, and naming the fixture there prints the same line twice. Reported
 * from live use: "Real Madrid vs Real Sociedad 1,39" above "Real Madrid vs Real
 * Sociedad 7,13", where the actual question was which side. So the fixture is
 * used only where it is the thing that differs, and everything below is what
 * happens when it is not.
 *
 * An accumulator has no one fixture, and this is where it gets awkward. A price
 * search walks combinations, so the three it comes back with tend to share most
 * of their legs — the first draft named the first two picks and produced three
 * chips reading "NY Knicks + Under 228.5 +1", differing only in their price.
 * Technically accurate and useless to choose from.
 *
 * So what is named is what is *not* shared: the first pick this slip has that
 * its siblings do not. That is the whole of the decision in front of the
 * bettor. Where they genuinely are alike all the way down, the first picks
 * stand in and the price does the discriminating, which by then is honest —
 * they really are the same bet at different odds.
 */
/**
 * The legs of this choice that its siblings do not all have: the decision itself.
 *
 * `by` is what counts as "the same leg" — the side picked, or the fixture it is
 * in. They give different answers and both are needed: two readings of one team
 * playing twice pick the same side in different matches, so by side nothing is
 * distinct and by fixture everything is.
 *
 * Where nothing is distinct the whole slip stands in, which is what makes the
 * caller's fallback chain terminate honestly.
 */
function decidingLegs(
  slip: WidgetViewModel['slips'][number],
  siblings: WidgetViewModel['slips'],
  by: (leg: WidgetViewModel['slips'][number]['legs'][number]) => string = (leg) =>
    leg.selectionLabel,
): WidgetViewModel['slips'][number]['legs'] {
  const others = siblings.filter((other) => other.id !== slip.id);
  const shared = (leg: WidgetViewModel['slips'][number]['legs'][number]): boolean =>
    others.every((other) => other.legs.some((their) => by(their) === by(leg)));

  const distinct = slip.legs.filter((leg) => !shared(leg));
  return distinct.length > 0 ? distinct : slip.legs;
}

/** Two names at most, then a count: a chip has to be readable at a glance. */
function joinPicks(names: string[]): string {
  if (names.length <= 2) return names.join(' + ');
  return `${names.slice(0, 2).join(' + ')} +${names.length - 2}`;
}

function choiceLabel(
  slip: WidgetViewModel['slips'][number],
  siblings: WidgetViewModel['slips'],
): string {
  const others = siblings.filter((other) => other.id !== slip.id);

  // The fixture, but only when no sibling shares it. Otherwise it is the one
  // thing about this choice that carries no information at all.
  if (slip.eventLabel !== null && others.every((other) => other.eventLabel !== slip.eventLabel)) {
    return slip.eventLabel;
  }

  return joinPicks(decidingLegs(slip, siblings).map((leg) => leg.selectionLabel));
}

/**
 * The labels for a whole set of choices, each different from the others.
 *
 * `choiceLabel` names what this choice does not share with its siblings, which
 * is the right answer until two markets on one fixture pick the same side.
 * Seen live: "Liverpool Fc 1,52" directly above "Liverpool Fc 1,79" — the same
 * three words twice, and the bettor left to guess which of the two the price
 * belonged to.
 *
 * Where that happens the market is what they are choosing between, so the
 * market is what gets added. Only to the labels that actually collide: a set
 * where every line already reads differently is not improved by writing
 * "Match result" after each of them.
 */
function choiceLabels(slips: WidgetViewModel['slips']): string[] {
  const base = slips.map((slip) => choiceLabel(slip, slips));
  const deciding = slips.map((slip) => decidingLegs(slip, slips));

  /** What each colliding line would read as, if it named this instead. */
  const byFixture = (index: number): string => {
    const slip = slips[index];
    if (slip === undefined) return '';
    // Keyed on the fixture, not the side: the side is what they have in common.
    return joinPicks(
      decidingLegs(slip, slips, (leg) => leg.eventLabel).map((leg) => leg.eventLabel),
    );
  };
  const byMarket = (index: number): string => {
    const market = deciding[index]?.[0]?.marketLabel ?? '';
    return market === '' ? (base[index] ?? '') : `${base[index] ?? ''} · ${market}`;
  };

  return base.map((label, index) => {
    const twins = base.map((_, at) => at).filter((at) => base[at] === label);
    if (twins.length < 2) return label;

    /**
     * What settles it, in the order it settles things.
     *
     * The fixture first, because a side that plays twice is the common case
     * and the match is what the bettor is choosing between — "Real Madrid +
     * Liverpool Fc" printed twice, once for each Liverpool fixture, was the
     * live report. Then the market, for two readings of one match that pick
     * the same side. Neither, and the price does it — same match, same side,
     * same market, different price is honest, and writing "Match result"
     * after both lines would lengthen them without settling anything.
     */
    const fixture = byFixture(index);
    if (fixture !== '' && twins.some((at) => byFixture(at) !== fixture)) return fixture;

    const market = byMarket(index);
    if (market !== label && twins.some((at) => byMarket(at) !== market)) return market;

    return label;
  });
}

function renderChoices(
  slips: WidgetViewModel['slips'],
  lang: string,
  odds: OddsFormat,
  enabled: boolean,
): ElementSpec {
  const labels = choiceLabels(slips);

  return el('div', {
    className: 'geni-choices',
    children: slips.map((slip, index) =>
      el('button', {
        className: 'geni-choice',
        attrs: {
          type: 'button',
          'data-slip-id': slip.id,
          // Carried on the button so the transcript can record what was
          // pressed — see SLIP_CHOSEN. An attribute rather than a DOM read,
          // because the label is decided here, against the siblings.
          'data-choice-label': labels[index] ?? '',
          ...(enabled ? {} : { disabled: 'disabled' }),
        },
        children: [
          el('span', { className: 'geni-choice-label', text: labels[index] ?? '' }),
          el('span', {
            className: 'geni-choice-price',
            text: formatPrice(slip.totalPrice, lang, odds),
          }),
        ],
      }),
    ),
  });
}

/**
 * More to add, under the slip it belongs to.
 *
 * Three chips and a line to type on. The chips are what the server judged most
 * likely and cost nothing to press — no model, no language, no waiting. The
 * line is for everything else, and goes the ordinary way, through the model.
 *
 * Deliberately smaller than the choice chips above: those are a decision
 * between four matches and carry a fixture name, these sit under a slip the
 * bettor has already settled on and are an afterthought offered in passing. A
 * row that competed with the confirm button would be a row that got pressed by
 * mistake on the screen where pressing something moves money.
 */
function renderBuildOn(
  buildOn: NonNullable<WidgetViewModel['buildOn']>,
  lang: string,
  odds: OddsFormat,
): ElementSpec {
  /**
   * The disclosure and what it holds are one box, not two.
   *
   * Drawn as a separate block appearing underneath, opening read as another
   * thing arriving on the panel rather than as this thing opening. Sharing a
   * border and a background makes it one control that grows, which is what a
   * press on it should look like.
   */
  /**
   * The disclosure exists only when there is something behind it.
   *
   * A fixture the operator prices thinly quotes one market, and once the leg
   * on the slip is taken out of it there is nothing left to offer. "Quick
   * options" that open on an empty box is a control that lies about itself —
   * so it is absent, and the field and the add-a-match row below carry the row
   * on their own. They never depended on the chips in the first place.
   */
  const inside: ElementSpec[] = buildOn.options.length === 0 ? [] : [
    el('button', {
      className: `geni-build-toggle${buildOn.open ? ' geni-build-toggle--open' : ''}`,
      attrs: { type: 'button', 'aria-expanded': buildOn.open ? 'true' : 'false' },
      children: [
        el('span', { className: 'geni-build-toggle-label', text: buildOn.openLabel }),
        // A chevron rather than a character, so it turns rather than swaps: a
        // glyph that changes is a different thing appearing, a shape that
        // rotates is the same thing opening.
        // `svg`, not `span`. `createElementNS(SVG_NAMESPACE, 'span')` builds an
        // SVG element *named* span, which is not a drawing surface: the path
        // inside is correct and the box measures 0×0, so the glyph is simply
        // absent. It read as a styling problem for as long as it shipped.
        el('svg', {
          className: 'geni-build-caret',
          ns: SVG_NAMESPACE,
          attrs: { viewBox: '0 0 12 12', width: '12', height: '12', 'aria-hidden': 'true' },
          children: [
            el('path', {
              ns: SVG_NAMESPACE,
              attrs: { d: 'M2.5 4.5 6 8l3.5-3.5', fill: 'none', 'stroke-width': '1.6' },
            }),
          ],
        }),
      ],
    }),
  ];

  if (buildOn.open) {
    inside.push(
      el('div', {
        className: 'geni-build-options',
        children: buildOn.options.map((option) =>
          el('button', {
            className: `geni-build-chip${option.picked ? ' geni-build-chip--picked' : ''}`,
            attrs: {
              type: 'button',
              'data-ref': option.ref,
              'aria-pressed': option.picked ? 'true' : 'false',
            },
            children: [
              el('span', { className: 'geni-build-chip-label', text: option.label }),
              el('span', {
                className: 'geni-build-chip-price',
                text: formatPrice(option.price, lang, odds),
              }),
            ],
          }),
        ),
      }),
    );

    /**
     * The confirm exists only once something is ticked.
     *
     * Absent rather than disabled: a greyed button on a panel somebody has just
     * opened is an instruction they have not been given, and the panel already
     * says what to do by having things to tick.
     */
    if (buildOn.pickedCount > 0) {
      inside.push(
        el('button', {
          className: 'geni-build-confirm',
          attrs: { type: 'button' },
          text: `${buildOn.confirmLabel} (${buildOn.pickedCount})`,
        }),
      );
    }
  }

  return el('div', {
    className: 'geni-build',
    children: [
      // Always shown, both of them. The heading says what the row is for before
      // anything is opened, and the field is the way out for a bet the options
      // did not think of — hiding it behind a disclosure would hide the only
      // part that always works.
      el('p', { className: 'geni-build-title', text: buildOn.title }),
      ...(inside.length === 0
        ? []
        : [
            el('div', {
              className: `geni-build-box${buildOn.open ? ' geni-build-box--open' : ''}`,
              children: inside,
            }),
          ]),
      /**
       * Two rows, because they are two different acts.
       *
       * The field is the way out for a market on the fixture already on the
       * ticket — the same act as the chips above it, for the one the chips did
       * not think of. The button under it adds a *different* fixture to the
       * accumulator, which is not the same question and cannot share a box
       * with it: somebody who has just put Real Madrid on and wants Liverpool
       * as well is not looking for another market on Real Madrid.
       *
       * They were briefly one control, and that was wrong in both directions —
       * it took the free-text field away from the row it belonged to, and it
       * made "add another match" the only way to type anything at all.
       */
      el('div', {
        className: 'geni-build-add',
        children: [
          buildRow('input', buildOn.placeholder, buildOn.title),
          /**
           * The invitation, which becomes the field it is offering.
           *
           * It used to sit under a field that was always on screen and send
           * *that* field, so pressing it moved the cursor a centimetre and
           * changed nothing else — reported exactly that way. Now it is where
           * its own box will be, and pressing it puts the box there.
           */
          buildOn.typing
            ? buildRow('new', buildOn.addPlaceholder, buildOn.addLabel)
            : el('button', {
                className: 'geni-build-add-button',
                attrs: { type: 'button' },
                children: [
                  // Two strokes rather than a "+" character, for the reason the
                  // caret above is a chevron: a glyph is at the mercy of
                  // whatever font the operator's page sets, and this one has to
                  // stay square.
                  el('svg', {
                    className: 'geni-build-plus',
                    ns: SVG_NAMESPACE,
                    attrs: {
                      viewBox: '0 0 12 12',
                      width: '12',
                      height: '12',
                      'aria-hidden': 'true',
                    },
                    children: [
                      el('path', {
                        ns: SVG_NAMESPACE,
                        attrs: { d: 'M6 2.6v6.8M2.6 6h6.8', fill: 'none', 'stroke-width': '1.6' },
                      }),
                    ],
                  }),
                  el('span', { className: 'geni-build-add-label', text: buildOn.addLabel }),
                ],
              }),
        ],
      }),
    ],
  });
}

/**
 * A field and the arrow that sends it.
 *
 * The arrow is not decoration and not a second way to do the same thing:
 * without it the row is a text box with no visible way out, and the only
 * sentence anybody could send is one they guessed to press Enter on. It is the
 * same pair as the composer at the foot of the panel, which is where the
 * bettor has already learnt what the arrow does.
 *
 * Both rows carry their own, and the handler reads the field beside the arrow
 * that was pressed rather than the first one on the panel — which is the bug
 * this shape would otherwise have: two boxes, one of them sending the other.
 */
function buildRow(name: 'input' | 'new', placeholder: string, label: string): ElementSpec {
  const field = `geni-build-${name}`;

  return el('div', {
    className: 'geni-build-row',
    children: [
      el('input', {
        className: `geni-build-field ${field}`,
        attrs: { type: 'text', placeholder, 'aria-label': label },
      }),
      el('button', {
        className: `geni-send geni-build-send geni-build-send-${name}`,
        // Which box this arrow sends, named on the arrow itself. Walking up to
        // the row and back down would say the same thing in a shape the fake
        // DOM the tests run on does not have.
        attrs: { type: 'button', 'aria-label': label, 'data-field': field },
        children: [el('span', { className: 'geni-send-label', text: label }), icon(SEND_ICON)],
      }),
    ],
  });
}

function renderSlip(
  slip: WidgetViewModel['slips'][number],
  lang: string,
  odds: OddsFormat,
  /**
   * Whether this slip's own confirm button is wanted.
   *
   * False while the stake flow is running, and that is a safety point rather
   * than a tidiness one: the slip's button hands the bet to the operator's
   * external slip, and the card's places it here. Two buttons a centimetre
   * apart doing different things with somebody's money is not a layout
   * problem.
   */
  showConfirm = true,
): ElementSpec {
  const children: ElementSpec[] = [];

  // The fixture and the kind of bet, above the legs. `eventLabel` is null for
  // an accumulator across several matches, and then the row is just the kind —
  // naming one match while pricing three would be worse than naming none.
  children.push(
    el('div', {
      className: 'geni-slip-head',
      children: [
        ...(slip.eventLabel === null
          ? []
          : [el('span', { className: 'geni-slip-event', text: slip.eventLabel })]),
        el('span', { className: 'geni-slip-count', text: slip.combinationLabel }),
      ],
    }),
  );

  if (slip.priceMoved !== null) {
    children.push(
      el('p', {
        className: 'geni-price-moved',
        attrs: { role: 'status' },
        children: [
          el('span', { className: 'geni-price-moved-notice', text: slip.priceMoved.notice }),
          el('span', {
            className: 'geni-price-moved-previous',
            text: formatPrice(slip.priceMoved.previousTotalPrice, lang, odds),
          }),
          el('span', { className: 'geni-price-moved-action', text: slip.priceMoved.action }),
        ],
      }),
    );
  }

  children.push(
    el('ul', {
      className: 'geni-legs',
      children: slip.legs.map((leg) =>
        el('li', {
          className: 'geni-leg',
          children: [
            // Only where the legs disagree about it. When they are all the same
            // match the heading above already says so, and repeating it on
            // every leg is the same noise as printing a player market's name
            // twice.
            ...(slip.eventLabel !== null
              ? []
              : [el('span', { className: 'geni-leg-event', text: leg.eventLabel })]),
            // Both are shown, but only once when they say the same thing —
            // player markets have "Bellingham to score" for each.
            ...(leg.marketLabel === leg.selectionLabel || leg.marketLabel === ''
              ? [el('span', { className: 'geni-leg-selection', text: leg.selectionLabel })]
              : [
                  el('span', { className: 'geni-leg-market', text: leg.marketLabel }),
                  el('span', { className: 'geni-leg-selection', text: leg.selectionLabel }),
                ]),
            el('span', { className: 'geni-leg-price', text: formatPrice(leg.price, lang, odds) }),
          ],
        }),
      ),
    }),
    el('p', {
      className: 'geni-total',
      children: [
        el('span', { className: 'geni-total-label', text: slip.totalPriceLabel }),
        el('span', { className: 'geni-total-price', text: formatPrice(slip.totalPrice, lang, odds) }),
      ],
    }),
  );

  if (showConfirm) {
    children.push(
      el('button', {
        className: 'geni-confirm',
        text: slip.confirmLabel,
        attrs: {
          type: 'button',
          'data-slip-id': slip.id,
          ...(slip.confirmEnabled ? {} : { disabled: 'disabled' }),
        },
      }),
    );
  }

  return el('section', { className: 'geni-slip', children });
}

function renderComposer(vm: WidgetViewModel): ElementSpec {
  return el('form', {
    className: 'geni-composer',
    children: [
      el('input', {
        className: 'geni-input',
        attrs: {
          type: 'text',
          'aria-label': vm.placeholder,
          placeholder: vm.placeholder,
          autocomplete: 'off',
          ...(vm.inputEnabled ? {} : { disabled: 'disabled' }),
        },
      }),
      el('button', {
        className: 'geni-send',
        attrs: {
          type: 'submit',
          'aria-label': vm.sendLabel,
          ...(vm.inputEnabled ? {} : { disabled: 'disabled' }),
        },
        children: [
          el('span', { className: 'geni-send-label', text: vm.sendLabel }),
          icon(SEND_ICON),
        ],
      }),
    ],
  });
}

/**
 * The whole widget for a given view model.
 *
 * Deliberately says nothing about the entrance animation. The panel carries
 * `geni-panel--entering` only on the paint that first draws it, and that marker
 * is added by the element after it has decided whether to paint at all — it
 * has to be, because the element skips a paint whose output matches what is
 * already on screen, and a one-shot marker inside the tree would make every
 * arrival differ from the repaint after it and defeat that comparison. See
 * `repaint()`.
 */
export function renderWidget(vm: WidgetViewModel): ElementSpec {
  if (!vm.open) return renderLauncher(vm.launcherLabel, vm.teaser, vm.launcherHint);

  const children: ElementSpec[] = [...materialLayers(), renderHeader(vm)];

  // Ahead of the conversation, because a bettor who is told the assistant is
  // unavailable should not have to read past a transcript to find out.
  if (vm.unavailable !== null) {
    children.push(
      el('p', { className: 'geni-unavailable', text: vm.unavailable, attrs: { role: 'status' } }),
    );
  }

  // Everything that grows goes in one scroller, so the header, the composer and
  // the RG footer stay put while a long conversation moves underneath them.
  const scroll: ElementSpec[] = [renderMessages(vm)];

  // Above the thinking indicator and everything else, because on an empty
  // conversation there is nothing else — this is what the bettor opens onto.
  //
  // The introduction comes first and the chips under it, which is the order the
  // two are read in: what this is, then what to try. Written strings, both of
  // them, so opening the panel costs nothing at all.
  if (vm.greeting !== null) {
    scroll.push(el('p', { className: 'geni-greeting', text: vm.greeting }));
  }
  if (vm.starters !== null) scroll.push(renderStarters(vm.starters));

  if (vm.thinkingLabel !== null) scroll.push(renderThinking(vm.thinkingLabel));

  if (vm.slipTitle !== null) {
    scroll.push(el('h3', { className: 'geni-slip-title', text: vm.slipTitle }));
  }

  // Three arrangements, and which one is drawn says where the bettor is.
  //
  //   no stake flow     the slip card, with its own confirm button — the
  //                     handoff, exactly as it was before in-chat betting
  //   amount still open the slip card without its button, then the stake step
  //   amount settled    the confirmation card alone
  //
  // The last one drops the slip card rather than stacking the two. They carry
  // the same legs and the same price, so showing both puts two near-identical
  // surfaces one above the other and leaves the bettor working out which one
  // is the live thing — on the screen where pressing something moves money.
  const stakeFlow = vm.stake !== null;
  if (vm.betCard === null) {
    if (vm.slips.length > 1) {
      // More than one on offer is a question, not an answer. See renderChoices.
      scroll.push(renderChoices(vm.slips, vm.lang, vm.oddsFormat, vm.slips[0]!.confirmEnabled));
    } else {
      for (const slip of vm.slips) {
        scroll.push(renderSlip(slip, vm.lang, vm.oddsFormat, !stakeFlow));
      }
    }
  }

  // Between the slip and the amount, which is where it belongs: it builds on
  // what is above it and must be settled before anybody names a figure.
  if (vm.buildOn !== null && vm.betCard === null) {
    scroll.push(renderBuildOn(vm.buildOn, vm.lang, vm.oddsFormat));
  }

  if (vm.stake !== null) scroll.push(renderStake(vm.stake, vm.lang));
  if (vm.betCard !== null) scroll.push(renderBetCard(vm.betCard, vm.lang, vm.oddsFormat));

  if (vm.placementNotice !== null) {
    scroll.push(
      el('p', {
        className: 'geni-placement',
        text: vm.placementNotice,
        attrs: { role: 'status' },
      }),
    );
  }

  if (vm.notice !== null) {
    scroll.push(el('p', { className: 'geni-notice', text: vm.notice, attrs: { role: 'status' } }));
  }

  if (vm.error !== null) {
    scroll.push(el('p', { className: 'geni-error', text: vm.error, attrs: { role: 'alert' } }));
  }

  // Last thing under the answer, and only where there is an answer worth
  // rating. Deliberately after the slip and the stake step rather than between
  // them: it is a comment on what was just done, not part of doing it.
  if (vm.feedback !== null) scroll.push(renderFeedback(vm.feedback));

  children.push(el('div', { className: 'geni-scroll', children: scroll }), renderComposer(vm));

  if (vm.feedbackForm !== null) children.push(renderFeedbackForm(vm.feedbackForm));

  // Last in the DOM, because it is standing advice rather than part of the
  // exchange — and screen readers should reach it after the answer, not before.
  if (vm.responsibleGambling !== null || vm.feedbackOpenLabel !== null) {
    const footer: ElementSpec[] = [];
    const rg = vm.responsibleGambling;

    /**
     * The full wording, above the buttons rather than below them.
     *
     * Expanding downward would push the two controls off the bottom of a short
     * panel and move the thing that was just pressed out from under the finger.
     */
    if (rg?.full != null) {
      footer.push(
        el('div', {
          className: 'geni-rg-full',
          attrs: { role: 'region' },
          children: [
            el('p', { className: 'geni-rg-message', text: rg.full.message }),
            el('p', { className: 'geni-rg-age', text: rg.full.ageNotice }),
          ],
        }),
      );
    }

    const controls: ElementSpec[] = [];

    /**
     * Feedback on the left, responsible gambling on the right.
     *
     * The standing way in is anchored here rather than under the greeting, and
     * that is not a style choice: the greeting is null the moment the
     * conversation starts, so a link living there would be reachable only
     * before the bettor had anything to say — and the person worth hearing
     * from is the one whose fifth attempt just failed.
     */
    if (vm.feedbackOpenLabel !== null) {
      controls.push(
        el('button', {
          className: 'geni-feedback-open',
          text: vm.feedbackOpenLabel,
          attrs: { type: 'button' },
        }),
      );
    }

    if (rg !== null) {
      controls.push(
        el('button', {
          className: 'geni-rg-open',
          text: rg.full === null ? rg.openLabel : rg.full.closeLabel,
          attrs: { type: 'button', 'aria-expanded': rg.full === null ? 'false' : 'true' },
        }),
      );
    }

    if (controls.length > 0) {
      footer.push(el('div', { className: 'geni-footer-actions', children: controls }));
    }

    /**
     * The one line that is always on screen, last of all.
     *
     * `requireRgMessage` says the message must be *shown*, and a footer that
     * folded all of it behind a press would be answering a regulator's
     * question with "it is one tap away". So the short form stays put and only
     * the long form moves — it sits under the controls as the panel's closing
     * line rather than as a heading over them.
     */
    if (rg !== null) {
      footer.push(el('p', { className: 'geni-rg-short', text: rg.short }));
    }

    children.push(el('footer', { className: 'geni-rg', children: footer }));
  }

  return el('div', {
    className: 'geni-panel',
    // The language the strings actually came from, not the one that was asked
    // for. A screen reader announcing English text as Turkish is worse than
    // announcing it as English.
    attrs: { lang: vm.lang, role: 'dialog', 'aria-label': vm.title, 'aria-modal': 'false' },
    children,
  });
}
